import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ExternalLink, Library, ShieldCheck } from "lucide-react";
import { REFERENCE_SECTIONS, STANDARDS } from "@/lib/references";
import { KineticText, Reveal } from "@/components/motion/primitives";
import { scrollToEl, BackToTop, SectionNav } from "@/components/ChapterNav";
import Seo from "@/components/Seo";

function SourceCard({ item, i }) {
  return (
    <Reveal delay={Math.min(i, 6) * 0.05}>
      <article
        id={item.id}
        className="flex h-full scroll-mt-36 flex-col rounded-2xl border border-border bg-card p-5 transition-colors target:border-gold target:ring-1 target:ring-gold/40 sm:p-6"
        data-testid={`reference-${item.id}`}
      >
        <h3 className="font-serif text-xl font-semibold leading-snug text-foreground">{item.name}</h3>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.desc}</p>
        {item.use && (
          <p className="mt-3 text-sm leading-relaxed text-foreground/90">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">How we use it </span>
            <span className="block mt-1">{item.use}</span>
          </p>
        )}
        <a
          href={item.url}
          target="_blank"
          rel="noreferrer"
          data-testid={`reference-link-${item.id}`}
          className="mt-auto inline-flex items-center gap-1.5 self-start rounded-full border border-border bg-background px-4 py-2 pt-2 text-xs font-semibold text-foreground transition-colors hover:border-gold hover:bg-gold/10"
          style={{ marginTop: "1.25rem" }}
        >
          Visit {item.url.replace(/^https?:\/\/(www\.)?/, "").split("/")[0]} <ExternalLink className="h-3 w-3" />
        </a>
      </article>
    </Reveal>
  );
}

export default function References() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => scrollToEl(document.getElementById(hash.slice(1)), -130), 350);
    return () => clearTimeout(t);
  }, [hash]);

  const navItems = [...REFERENCE_SECTIONS.map((s) => ({ key: s.id, label: s.title, count: s.items.length })), { key: "standards", label: "Our standards", count: STANDARDS.length }];

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8" data-testid="references-page">
      <Seo title="References & Sources" description="The Bible texts, commentaries, lexicons, and study tools behind every entry on QuestionGod, and the standards we hold ourselves to." />
      <header className="mb-8">
        <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-gold"><Library className="h-4 w-4" /> How we study</span>
        <KineticText as="h1" text="References & Sources" className="mt-2 block font-serif text-4xl font-bold leading-[1.05] tracking-tight text-foreground sm:text-5xl" testId="references-headline" />
        <Reveal delay={0.3}>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            Every entry on QuestionGod is built from the biblical text and checked against trusted commentaries, lexicons, and interlinear tools. We read Scripture in three steps: first what it says, then what it meant to its original hearers, and only then what it means for us. These are the resources we use.
          </p>
        </Reveal>
      </header>

      <SectionNav items={navItems} idFor={(k) => `refs-${k}`} label="Jump to" testId="references-nav" />

      <div className="space-y-14">
        {REFERENCE_SECTIONS.map((s) => (
          <section key={s.id} id={`refs-${s.id}`} data-section={s.id} className="scroll-mt-32" data-testid={`references-section-${s.id}`}>
            <div className="mb-5 flex items-center gap-3">
              <h2 className="font-serif text-2xl font-semibold text-foreground sm:text-3xl">{s.title}</h2>
              <span className="h-px flex-1 bg-border" />
              <span className="font-mono text-xs text-muted-foreground">{s.items.length}</span>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {s.items.map((item, i) => <SourceCard key={item.id} item={item} i={i} />)}
            </div>
          </section>
        ))}
        <section id="refs-standards" data-section="standards" className="scroll-mt-32" data-testid="references-section-standards">
          <div className="mb-5 flex items-center gap-3">
            <h2 className="font-serif text-2xl font-semibold text-foreground sm:text-3xl">Our standards</h2>
            <span className="h-px flex-1 bg-border" />
          </div>
          <Reveal>
            <div id="standards" className="grain relative overflow-hidden rounded-2xl bg-secondary p-6 text-secondary-foreground sm:p-8">
              <ul className="relative space-y-4">
                {STANDARDS.map((line, i) => (
                  <li key={line} className="flex items-start gap-3" data-testid={`standard-${i + 1}`}>
                    <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                    <p className="font-serif text-lg leading-snug text-white sm:text-xl">{line}</p>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mx-auto mt-10 max-w-3xl text-center font-serif text-lg italic leading-relaxed text-foreground/85 sm:text-xl" data-testid="references-feedback">
              We welcome feedback and we aim to provide accurate resources for everyone who uses this site. We aren&apos;t above correction; we are lifelong learners. Help us get it right, share a different perspective, or send us feature requests and any ideas you have.
            </p>
          </Reveal>
        </section>
      </div>
      <BackToTop />
    </div>
  );
}
